import {
    Row,
    Col,
    Table,
    useTable,
    Card,
    List,
    Space,
    EditButton,
    Tag,
    DeleteButton,
    Button,
} from "@pankod/refine-antd";

import {HttpError, IResourceComponentsProps} from "@pankod/refine-core";

import {IProductBundle} from "../../Interfaces";
import {Fragment} from "react";
import {formatCurrency} from "../Dashboard/FormatCurrency";
import {ProductImageThumbnailRender} from "../Products/ProductImageThumbnailRender";
import {syncBundlesToMeilisearch} from "../../Services/productServices";
import toast from "react-hot-toast";
import {getBestImageUrl} from "./Components/imageUrl";


export const ProductBundleList: React.FC<IResourceComponentsProps> = () => {
    const {tableProps} = useTable<IProductBundle, HttpError>({
        resource: `product-bundles`,
        initialPageSize: 100,
    });

    const parseQuantityTable = (qt: any): Record<string, number> => {
        if (!qt) return {};
        if (typeof qt === "string") {
            try {
                return JSON.parse(qt);
            } catch {
                return {};
            }
        }
        return qt as Record<string, number>;
    };

    const computeMemberTotal = (record: IProductBundle): number => {
        const qt = parseQuantityTable(record.quantityTable);
        return (record.products || []).reduce((sum, p) => {
            const qty = qt[p.masterSku] ?? 1;
            const price = p.variants?.[0]?.price ?? 0;
            return sum + price * qty;
        }, 0);
    };

    const statusColor = (status: string) => {
        if (status === "active") return "green";
        if (status === "archived") return "default";
        return "orange";
    };

    const getCoverImageUrl = (record: IProductBundle): string | undefined => {
        return getBestImageUrl(record.coverImage);
    };

    const handleSyncToMeilisearch = () => {
        toast.promise(syncBundlesToMeilisearch(), {
            loading: "Syncing bundles to Meilisearch...",
            success: (resp: any) => resp?.data?.message || "Bundles synced to Meilisearch",
            error: (err) => `Sync failed: ${err?.message || err}`,
        });
    };

    return (
        <Fragment>
            <Card>
                <Row style={{marginBottom: "30px"}} justify="space-between" align="middle">
                    <Col span={12}></Col>
                    <Col>
                        <Button onClick={handleSyncToMeilisearch}>Sync to Meilisearch</Button>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <List canCreate={true}>
                            <Table dataSource={tableProps?.dataSource} rowKey="id">
                                <Table.Column<IProductBundle>
                                    dataIndex="title"
                                    title="Title"
                                    render={(_, record) => {
                                        return <Fragment>
                                            <div style={{display: "flex", alignItems: "center", gap: 8}}>
                                                <ProductImageThumbnailRender
                                                    imageUrl={getCoverImageUrl(record)}
                                                />
                                                {record.title}
                                            </div>
                                        </Fragment>;
                                    }}
                                ></Table.Column>

                                <Table.Column<IProductBundle>
                                    dataIndex="products"
                                    title="Products"
                                    render={(_, record) => {
                                        const quantityTable = parseQuantityTable(record.quantityTable);
                                        return <Fragment>
                                            {record.products.map((product, index) => (
                                                <Tag
                                                    key={index}>{product.name} x{quantityTable[product.masterSku] ?? 1}</Tag>
                                            ))}
                                        </Fragment>;
                                    }}
                                ></Table.Column>

                                <Table.Column<IProductBundle>
                                    dataIndex="flat_price"
                                    title="Flat Price"
                                    render={(_, record) => {
                                        return <Fragment> {formatCurrency(record.flat_price)}</Fragment>;
                                    }}
                                ></Table.Column>

                                <Table.Column<IProductBundle>
                                    title="Saving"
                                    render={(_, record) => {
                                        const memberTotal = computeMemberTotal(record);
                                        const saving = memberTotal - record.flat_price;
                                        if (saving > 0) {
                                            return <Tag color="green">{formatCurrency(saving)}</Tag>;
                                        }
                                        return <span style={{color: "#999"}}>—</span>;
                                    }}
                                ></Table.Column>

                                <Table.Column<IProductBundle>
                                    dataIndex="status"
                                    title="Status"
                                    render={(_, record) => (
                                        <Tag color={statusColor(record.status)}>{record.status || "draft"}</Tag>
                                    )}
                                ></Table.Column>

                                <Table.Column<IProductBundle>
                                    title="Actions"
                                    dataIndex="actions"
                                    render={(_, record) => (
                                        <Space>
                                            <EditButton size="small" recordItemId={record.id}/>
                                            <DeleteButton size="small" recordItemId={record.id}/>
                                        </Space>
                                    )}
                                />
                            </Table>
                        </List>
                    </Col>
                </Row>
            </Card>
        </Fragment>
    );
};
